/** Shape of the demo store as persisted in localStorage. Mirrors the database tables loosely. */
import type { AircraftClass, AircraftView, BaseView, DamageCategory, DamageStatus, NoticeLevel } from '@fsv/core';
import type { EntityType, EventAction, JsonObject, Settings, SignInProvider, TenantContact, TenantRole } from './types';

/** The drawing a view of an own type uses: an upload, with the size of the ORIGINAL file (what pins are measured against). */
export interface StoredTypeDrawing {
  uploadId: string;
  width: number;
  height: number;
}

export interface StoredType {
  id: string;
  /** null: the platform catalogue, usable by every club; else the club's own type (fleet admin). */
  tenantId: string | null;
  code: string;
  name: string;
  credit: string;
  /** 0025; null = unknown (an own type from before the class existed). */
  aircraftClass: AircraftClass | null;
  mirrorRules: Partial<Record<AircraftView, BaseView>>;
  /** Own types only; the catalogue's drawings come from the injected loader. */
  drawings: Partial<Record<AircraftView, StoredTypeDrawing>>;
  createdAt: string;
}

/**
 * An uploaded drawing, kept like the API keeps its blobs: never overwritten,
 * so a replacement can be undone and a pins_drift answer confirmed without a
 * second upload. `src` is a downscaled copy (≤ 800 px, localStorage holds no
 * 5-MB originals) as a data URL, or `demo:<CODE>:<view>` for a drawing of the
 * free demo set resolved through the loader; width and height are the
 * original's, read from its IHDR.
 */
export interface StoredDrawingUpload {
  id: string;
  tenantId: string;
  typeId: string;
  view: AircraftView;
  src: string;
  width: number;
  height: number;
  alpha: boolean;
  createdAt: string;
}

export interface StoredAircraft {
  id: string;
  tenantId: string;
  registration: string;
  typeId: string;
  isActive: boolean;
  sortOrder: number;
  deletedAt: string | null;
  deletedBy: string | null;
}

/** A club export as listed; the archive itself is rebuilt on demand and never stored. */
export interface StoredExport {
  tenantId: string;
  name: string;
  size: number;
  createdAt: string;
  createdBy: string;
}

export interface StoredDamage {
  id: string;
  tenantId: string;
  number: number;
  aircraftId: string;
  /** One or more, catalogue order (like the API since 0021). */
  categories: DamageCategory[];
  view: AircraftView;
  x: number;
  y: number;
  title: string;
  description: string | null;
  status: DamageStatus;
  reportedBy: string;
  reportedAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  mergedIntoId: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface StoredImage {
  id: string;
  damageId: string;
  /**
   * A data URL, "placeholder:<category>:<seed>:<overview 0|1>" or "photo:<name>[:thumb]"
   * (demoPhotos.ts), the last two resolved at read time.
   */
  url: string;
  thumbUrl: string;
  width: number;
  height: number;
  isOverview: boolean;
  /**
   * 'video' (stage 2): `url` is a blob: URL that lives as long as the page — a
   * video does not fit into the browser storage the demo keeps. Videos are
   * dropped when the stored state is read back (`dropSessionVideos`).
   */
  kind?: 'photo' | 'video';
  durationMs?: number | null;
  uploadedBy: string;
  createdAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface StoredComment {
  id: string;
  damageId: string;
  authorId: string;
  body: string;
  createdAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

/** A notice of the technicians on an aircraft (dbo.aircraft_notices, 0022). */
export interface StoredNotice {
  id: string;
  tenantId: string;
  aircraftId: string;
  level: NoticeLevel;
  title: string;
  body: string | null;
  validUntil: string | null;
  createdAt: string;
  createdBy: string;
  revisedAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

/** „Gelesen“ (dbo.aircraft_notice_acks): one per notice and person, re-stamped on a revised notice. */
export interface StoredNoticeAck {
  noticeId: string;
  memberId: string;
  acknowledgedAt: string;
}

export interface StoredEvent {
  id: number;
  tenantId: string;
  actorId: string | null;
  entityType: EntityType;
  entityId: string;
  entityLabel: string;
  damageId: string | null;
  action: EventAction;
  before: JsonObject | null;
  after: JsonObject | null;
  reason: string | null;
  undoOf: number | null;
  createdAt: string;
}

/** A person across tenants (members table after ADR 0004: no role column). */
export interface StoredPerson {
  id: string;
  email: string;
  displayName: string;
  lastSignInAt: string | null;
  createdAt: string;
  providers: SignInProvider[];
  /** Profile picture as a data URL (the demo keeps it on the device); absent or null: initials. */
  avatar?: string | null;
  /** Live further sign-in addresses (0023); a removed one is gone here (the API keeps a soft-deleted row). */
  additionalEmails?: { id: string; email: string; source: 'club' | 'self'; createdAt: string }[];
  /** An address the persona is confirming; `blocked`: another person's — no code ever matches, like the API. */
  pendingEmail?: { email: string; expiresAt: string; blocked: boolean } | null;
}

export interface StoredMembership {
  tenantId: string;
  memberId: string;
  role: TenantRole;
  isActive: boolean;
  createdAt: string;
  /** Optional: stored states from before 0020 have none. */
  invitation?: { state: 'pending' | 'sent' | 'failed'; at: string } | null;
}

/** The tenant row. */
export interface StoredTenant extends TenantContact {
  id: string;
  name: string;
  slug: string;
  /** Prefix of damage references (FSV-000042); fixed at creation like the slug. */
  code: string;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  /** The club's logo as a data URL (0019). Optional: a state stored before it simply has none, so no version bump. */
  logo?: string | null;
}

/**
 * 4: tenants (ADR 0004). 5: tenant codes and damage references FSV-000042.
 * 6: fictional marketing seed (Musterstadt/Beispieltal, D-EX.., example.org).
 * 7: freely licensed demo drawings, pins moved onto them, new credits.
 * 8: fleet admin (own types with uploads, deleted aircraft) and club exports.
 * 9: settings.memberParticipation replaces settings.membersCanReport.
 * 10: no platform manager (Petra Plattform, her membership and her tenant
 *     events are gone; a stored 9 would still list her as a club admin).
 * A stored state of another version is discarded and re-seeded.
 */
// 12: the seed has no „hinten“ damage any more (09.10.2026).
// 13: a damage has `categories` (several types) instead of `category` (17.10.2026).
// 14: notices of the technicians and their „gelesen“ (0022), on top of 13.
// 16: real photos on eight Musterstadt damages, their titles and texts adjusted (29.10.2026).
// 17: real photos on nine more Musterstadt damages, their titles, texts and pins adjusted (29.10.2026).
// 18: aircraft types carry their class (0025, `aircraftClass`).
export const DEMO_STATE_VERSION = 18;

export interface DemoState {
  version: typeof DEMO_STATE_VERSION;
  /**
   * Last damage number handed out, per tenant (dbo.damage_counters): every
   * club starts at 1, so FSV-000001 and LSVR-000001 both exist.
   */
  damageCounters: Record<string, number>;
  nextEventId: number;
  tenants: StoredTenant[];
  people: StoredPerson[];
  memberships: StoredMembership[];
  /** Platform catalogue (tenantId null) and the clubs' own types. */
  types: StoredType[];
  drawingUploads: StoredDrawingUpload[];
  aircraft: StoredAircraft[];
  damages: StoredDamage[];
  images: StoredImage[];
  comments: StoredComment[];
  events: StoredEvent[];
  settings: Record<string, Settings>;
  exports: StoredExport[];
  notices: StoredNotice[];
  noticeAcks: StoredNoticeAck[];
}
